import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock,ArrowRight, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedWidgets from '../components/FixedWidgets';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission (replace with actual Google Calendar API integration)
    setTimeout(() => {
      toast.success('Appointment request submitted successfully! We will contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      content: 'Deshpande Nagar, Hubballi, Karnataka 580029',
      color: 'from-[#4A90A4] to-[#6FCF97]'
    },
    {
      icon: Phone,
      title: 'Phone',
      content: '0836-2228390, +91 7996524417',
      color: 'from-[#6FCF97] to-[#4A90A4]'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'suvaidyamhospital@gmail.com',
      color: 'from-[#4A90A4] to-[#6FCF97]'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      content: '24/7 Emergency Services Available',
      color: 'from-[#6FCF97] to-[#4A90A4]'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FixedWidgets />

      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 to-[#4A90A4]/80" />
        <div className="relative z-10 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            Contact <span className="text-[#6FCF97]">Us</span>
          </h1>
          <p className="text-lg text-gray-200">
            Reach us easily — location and contact details below
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 text-center flex flex-col items-center"

              >
                <div className={`w-14 h-14 bg-gradient-to-br ${info.color} rounded-2xl flex items-center justify-center mb-4`}>
                  <info.icon className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{info.title}</h3>
                <p className="text-gray-600 text-sm">{info.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <motion.div
          {...fadeInUp}
          className="max-w-5xl mx-auto px-6 text-center"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ready to Experience
            <br />
            <span className="text-[#4A90A4]">World-Class Healthcare?</span>
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Our team of expert doctors and staff are here to provide you with the best medical care.
          </p>
          <a
                href="https://wa.me/917996524417?text=Hello%20I%20want%20to%20book%20an%20appointment"
                target="_blank"
                rel="noopener noreferrer"
              >
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 10px 40px rgba(74, 144, 164, 0.3)' }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-5 bg-gradient-to-r from-[#4A90A4] to-[#6FCF97] text-white rounded-full font-bold text-lg flex items-center space-x-3 mx-auto hover:shadow-2xl transition-all duration-300"
            >
              <span>Contact Us Today</span>
              <ArrowRight size={24} />
            </motion.button>
          </a>
        </motion.div>
      </section>

      {/* FULL WIDTH MAP SECTION */}
      <section className="w-full h-[75vh]">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d19464.130306658117!2d75.11586634292765!3d15.358713061892702!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bb8d7442d0b6c0f%3A0x8eb743e7733bf3e4!2sSUVAIDYAM%20MULTISPECIALITY%20HOSPITAL!5e0!3m2!1sen!2sin!4v1769414672664!5m2!1sen!2sin"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Hospital Location"
        />
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;